/**
 * Groups row cells into categories for the Single-Row Details View.
 *
 * Reads the row's columns + values + per-tab metadata (PK/FK flags) and
 * dispatches each column to `categorizeColumn` (a pure heuristic). Empty
 * groups are dropped; group order is stable (see `CATEGORY_ORDER`).
 *
 * Slice 1: name/type heuristics + FK/PK flags only. Slice 6 layers MetaInfo
 * (loose FKs / explicit category overrides) on top.
 */

import { useMemo } from 'react';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { RowCell, RowCellGroup, CategoryKey } from '../types/singleRowTypes';
import {
  categorizeColumn,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
} from '../utils/cellCategoryRules';

export type UseCellCategorizationParams = {
  readonly columns: ReadonlyArray<TableExplorerColumn>;
  readonly row: Record<string, unknown>;
  /**
   * Column NAMES the caller resolved as native-spatial (via
   * `categorizeSpatialColumn`) — those bucket into the `spatial` group (T11).
   * The rules file stays free of per-engine spatial type lists.
   */
  readonly spatialColumnNames?: ReadonlySet<string>;
};

export function useCellCategorization(
  params: UseCellCategorizationParams,
): ReadonlyArray<RowCellGroup> {
  const { columns, row, spatialColumnNames } = params;

  return useMemo(() => {
    if (columns.length === 0) return [];

    const buckets = new Map<CategoryKey, RowCell[]>();

    for (const column of columns) {
      const category = categorizeColumn({
        columnName: column.name,
        dataType: column.dataType ?? '',
        isPrimaryKey: column.isPrimaryKey,
        isForeignKey: column.isForeignKey,
        dataTypeIsSpatial: spatialColumnNames?.has(column.name) ?? false,
      });
      const cell: RowCell = {
        column,
        value: row[column.name],
        isForeignKey: column.isForeignKey ?? false,
      };
      const list = buckets.get(category);
      if (list) list.push(cell);
      else buckets.set(category, [cell]);
    }

    const groups: RowCellGroup[] = [];
    for (const key of CATEGORY_ORDER) {
      const cells = buckets.get(key);
      if (cells && cells.length > 0) {
        groups.push({ key, label: CATEGORY_LABELS[key], cells });
      }
    }
    return groups;
  }, [columns, row, spatialColumnNames]);
}
